import { memo, useEffect, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { usePermissions } from '@mint-lib/profile-helpers';

import {
  Accordion,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  Tooltip,
} from '@myn/mui';

import DateTime from '../../atoms/DateTime/DateTime.jsx';
import { FeaturePermissionsConstants } from '../../constants/FeaturePermissionsConstants.js';
import {
  useRefetchBuyingPlanInfo,
  useUpdateBuyingPlanInfoCache,
} from '../../hooks/useGetBuyingPlanData.js';
import { useGetVersionChannelMixData } from '../../hooks/useGetVersionChannelMixData.js';
import { useGetVersionData } from '../../hooks/useGetVersionData.js';
import { useSetIsArchivedVersion } from '../../hooks/useIsArchivedVersion.js';
import { Version } from '../../types/versions.js';
import VersionDetailsInfo from './VersionDetails/VersionDetailsInfo.jsx';
import VersionName from './VersionName.jsx';

export type Props = {
  item: Version;
  versions?: Version[];
  openedVersionId?: number | null;
  onNewVersionCreate: (versionID: number) => void;
  onOpenVersion: (version: Version) => void;
  onClickDelete: (id: number) => void;
};

const VersionsRow = memo(
  ({
    item,
    versions,
    openedVersionId,
    onNewVersionCreate,
    onOpenVersion,
    onClickDelete,
  }: Props) => {
    const { t } = useTranslation('@myn/planning');
    const [expanded, setExpanded] = useState(false);

    const { permissions } = usePermissions();
    const isRedOnlyAccess =
      permissions?.campaignPlanningFeature ===
        FeaturePermissionsConstants.FULL_CONTROL || false;
    const isNonFinUser = !permissions.financialDataFeature;

    const { data: channelMixVersionData } = useGetVersionChannelMixData({
      versionId: item.id,
      skip: !expanded,
    });
    const { refetch: getVersionData } = useGetVersionData({
      versionId: item.id,
    });

    const updateSelectedPlanCache = useUpdateBuyingPlanInfoCache();
    const refetchBuyingPlanInfo = useRefetchBuyingPlanInfo();
    const setIsArchivedVersion = useSetIsArchivedVersion();

    const shouldExpandCurrentVersion = !!item.isCurrent && !isNonFinUser;

    useEffect(() => {
      if (!shouldExpandCurrentVersion) {
        return;
      }

      setExpanded(true);
    }, [shouldExpandCurrentVersion]);

    const creationDate = item.createdAt ? new Date(item.createdAt) : null;
    const lastUpdateDate = item.lastUpdate ? new Date(item.lastUpdate) : null;

    const handleCreateClick = () => {
      onNewVersionCreate(item.id);
    };

    const handleOpenClick = async () => {
      onOpenVersion(item);
      const { data } = await getVersionData();

      if (item.isCurrent) {
        await refetchBuyingPlanInfo();
        setIsArchivedVersion(false);
        return;
      }

      if (data) {
        setIsArchivedVersion(true);
        const selectedPlanData = { ...data.mediaPlanData, version: data.name };
        updateSelectedPlanCache(selectedPlanData);
      }
    };

    const handleExpandedToggle = async () => {
      setExpanded((prev) => !prev);
    };

    return (
      <Card>
        <CardContent
          sx={{
            '&, :last-child': { paddingBottom: 1.5 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 3,
              marginBottom: expanded ? 3 : 0,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 3,
              }}
            >
              <VersionName
                variant={item.isCurrent ? 'active' : 'archived'}
                versionId={item.id}
                name={item.name}
              />

              {creationDate ? (
                <DateTime label={t('Creation')} date={creationDate} />
              ) : null}
              {lastUpdateDate ? (
                <DateTime label={t('Last update')} date={lastUpdateDate} />
              ) : null}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant="text"
                disabled={openedVersionId === item.id}
                data-testid={`open-version-btn-${item.id}`}
                onClick={handleOpenClick}
                startIcon="Launch"
              >
                {t('Open')}
              </Button>
              <Divider flexItem orientation="vertical" />
              <Tooltip title={t('Delete version')}>
                <IconButton
                  icon="Delete"
                  onClick={() => onClickDelete(item.id)}
                  disabled={!item.canBeDeleted || !isRedOnlyAccess}
                  data-testid={`delete-version-btn-${item.id}`}
                  variant="icon"
                  color="error"
                  size="large"
                />
              </Tooltip>
              <Tooltip title={t('Duplicate version')}>
                <IconButton
                  icon="Duplicate"
                  onClick={handleCreateClick}
                  disabled={!item.canBeDuplicated || !isRedOnlyAccess}
                  data-testid={`copy-version-btn-${item.id}`}
                  variant="icon"
                />
              </Tooltip>
              {isNonFinUser ? null : (
                <IconButton
                  icon={expanded ? 'ChevronUp' : 'ChevronDown'}
                  onClick={handleExpandedToggle}
                  role={`expand-accordion-btn-${item.id}`}
                  variant="icon"
                />
              )}
            </Box>
          </Box>
          {expanded ? (
            <Accordion
              role={`version-accordion-${item.id}`}
              sx={{ border: 'none' }}
            >
              <VersionDetailsInfo
                item={{ ...item, channelMixVersionData }}
                versions={versions}
              />
            </Accordion>
          ) : null}
        </CardContent>
      </Card>
    );
  },
);

export default VersionsRow;
